import React from 'react';
import {
  bool, func, shape, string, arrayOf
} from 'prop-types';
import { connect } from 'react-redux';
import {
  Link, useLocation, useParams
} from 'react-router-dom';
import { Translate } from 'react-localize-redux';

import styles from '../editor.module.css';
import Notification from '../../Notification';

import {
  getSuffixedRoute,
  isSurveySchemaPartType,
  getSurveyPartType
} from '../../../util';

import useSurveyPartEditor from './useSurveyPartEditor';
import {
  EDIT, NEW_VERSION, COPY, CREATE, IDENTIFIER, DESCRIPTION, SURVEY_TYPE
} from '../../../constants';

import { withLang } from '../../../i18n';
import WithSurveyPartValidation from '../../WithSurveyPartValidation';

import TextInput from '../../TextInput';
import ConfirmButton from '../../ConfirmButton';
import TabPane from '../../TabPane';
import SurveyPartItems from './SurveyPartItems';
import Variables from './Variables';
import TabsContainer from '../../TabsContainer';
import EditorInfoBox from '../SurveySchemaEditor/InfoBox';
import SurveyPartInfoBox from '../../SurveyPartView/InfoBox';
import InfoBox from '../../InfoBox';
import InfoRow from '../../InfoBox/InfoRow';
import { LocalizedTextArea } from '../../LocalizedInput';
import { usePreviousLocation } from '../../PreviousLocationProvider';
import { surveyTypeValues, surveyPartTypeValues } from '../../../constants/types';

const SurveyPartEditor = ({
  translate: t, isValid, invalidFields
}) => {
  const location = useLocation();
  const previousLocation = usePreviousLocation();
  const { surveyPartId } = useParams();

  const surveyPartType = getSurveyPartType(location.pathname);
  const previewRoute = `${location.pathname}/preview`;
  const translationBase = isSurveySchemaPartType(surveyPartType)
    ? 'surveySchemaParts'
    : 'extraSurveyParts';

  // Most heavy lifting (Redux, APIs, States, Handlers) are
  // handled in this custom hook for readability
  const {
    surveySchemaPart,
    fetchedSurveyPart,
    fetchedSurveyPartUsage,
    lockedFromEditing,
    codeInputValid,
    formCacheKey,
    actionMode,
    onChange,
    onLocalizedInputChange,
    onCodeInputBlur,
    cancelEdit
  } = useSurveyPartEditor({
    surveyPartId,
    surveyPartType,
    location,
    previousLocation,
    translationBase,
    t
  });

  const renderCodeInput = () => {
    const identifierErrorClass = codeInputValid ? '' : styles.error;
    return (
      <div className={styles.formItem}>
        <label htmlFor={IDENTIFIER}>{t('surveyParts.identifier')}</label>
        <div className={`${styles.errorContainer} ${identifierErrorClass}`}>
          <p>{t('editor.identifierError')}</p>
        </div>
        <TextInput
          className={identifierErrorClass}
          name={IDENTIFIER}
          value={surveySchemaPart.code || ''}
          placeholder={t(`${translationBase}.identifierPlaceholder`)}
          onChange={onChange}
          onBlur={onCodeInputBlur}
          maxLength={255}
          translate={t}
          dataCy="survey-schema-part-code-input"
        />
      </div>
    );
  };

  const renderDescriptionInput = () => (
    <div className={styles.formItem}>
      <h3>{t('surveyParts.description')}</h3>
      <LocalizedTextArea
        name={DESCRIPTION}
        values={surveySchemaPart.description || {}}
        onInputChange={onLocalizedInputChange}
        placeholderTranslationNamespace="inLanguage"
        maxLength={255}
        useAccordion
        dataCyPrefix="survey-schema-part-description-text-area"
      />
    </div>
  );

  const renderEditInfo = () => {
    const codeMap = {
      [NEW_VERSION]: { title: 'newVersionInformationTitle', text: 'newVersionInformation' },
      [COPY]: { title: 'newCopyInformationTitle', text: 'newCopyInformation' },
      [EDIT]: { title: 'limitedEditingInformationTitle', text: 'limitedEditingInformation' }
    };

    if (!codeMap[actionMode]) return null;

    const titleCode = `${translationBase}.${codeMap[actionMode].title}`;
    const textCode = `${translationBase}.${codeMap[actionMode].text}`;

    return <EditorInfoBox title={t(titleCode)} text={t(textCode)} />;
  };

  const renderLargeInfoBox = () => {
    const infoReady = fetchedSurveyPart && fetchedSurveyPartUsage;
    if (!infoReady) return null;

    return (
      <SurveyPartInfoBox
        surveyPart={fetchedSurveyPart}
        surveyPartUsage={fetchedSurveyPartUsage}
      />
    );
  };

  const renderSmallInfoBox = () => (
    <InfoBox>
      <InfoRow
        title={t('common.type')}
        content={t(`surveyType.${surveySchemaPart.surveyType}`)}
      />
    </InfoBox>
  );

  const renderSurveyPartComponents = () => {
    if (isSurveySchemaPartType(surveySchemaPart.type)) {
      return (
        <TabsContainer>
          <TabPane label={t('surveyParts.questions')}>
            <SurveyPartItems lockedFromEditing={lockedFromEditing} />
          </TabPane>
          <TabPane label={t('surveySchemaParts.dimensions.dimensions')}>
            <Variables lockedFromEditing={lockedFromEditing} />
          </TabPane>
        </TabsContainer>
      );
    }
    return <SurveyPartItems lockedFromEditing={lockedFromEditing} />;
  };

  const renderValidationErrors = () => (
    <div>
      {invalidFields.map((field) => (
        <div key={field} className={styles.validationErrorsContainer}>
          <Translate
            id={`surveyParts.validation.${field.type}.${field.problem}.${field.reason}`}
            data={{ value: field.value }}
          />
        </div>
      ))}
    </div>
  );

  const renderSurveyTypeSelect = () => (
    <div className={styles.contentColumn}>
      <div className={styles.formItem}>
        <label htmlFor={SURVEY_TYPE}>{t('common.type')}</label>
        <select
          name={SURVEY_TYPE}
          value={surveySchemaPart.surveyType}
          onChange={onChange}
          disabled={lockedFromEditing}
        >
          <option value="" disabled="disabled">{t('editor.surveySchema.typePlaceholder')}</option>
          {[...(Object.values(surveyTypeValues)), 'NOT_DEFINED'].map((type) => (
            <option key={type} value={type}>{t(`surveyType.${type}`)}</option>
          ))}
        </select>
      </div>
    </div>
  );

  const previewSurveyPartRoute = getSuffixedRoute(location.pathname, previewRoute);

  return (
    <div className="page-container">
      <h1>{t(`${translationBase}.surveyPart`)}</h1>
      {[CREATE, COPY].includes(actionMode) && (
        renderSmallInfoBox()
      )}
      {([NEW_VERSION, COPY].includes(actionMode) || lockedFromEditing) && (
        renderEditInfo()
      )}
      {[EDIT, NEW_VERSION].includes(actionMode) && (
        renderLargeInfoBox()
      )}

      {surveyPartType === surveyPartTypeValues.EXTRA_SURVEY_PART && (
        <Notification text={t('extraSurveyParts.sensitiveInfoNotification')} />
      )}

      <form className="form" key={formCacheKey}>
        {[NEW_VERSION, EDIT].includes(actionMode) && fetchedSurveyPart && fetchedSurveyPart.surveyType === 'NOT_DEFINED' && (
          renderSurveyTypeSelect()
        )}
        {[CREATE, COPY].includes(actionMode) && (
          renderCodeInput()
        )}
        {renderDescriptionInput()}
        {renderSurveyPartComponents()}
      </form>

      {!isValid && renderValidationErrors()}

      <div className={styles.navigation}>
        <ConfirmButton
          modalText={t('confirmButton.confirmCancel')}
          onConfirm={cancelEdit}
        >
          {t('common.cancel')}
        </ConfirmButton>
        <Link
          className={`button--action ${isValid && codeInputValid ? '' : styles.disabled}`}
          to={previewSurveyPartRoute}
          state={{
            isCreateNewVersion: actionMode === NEW_VERSION,
            isCreateNewCopy: actionMode === COPY
          }}
          data-cy="preview-and-save-button"
        >
          {t('common.previewAndSave')}
        </Link>
      </div>
    </div>
  );
};

SurveyPartEditor.propTypes = {
  isValid: bool.isRequired,
  invalidFields: arrayOf(shape({
    type: string.isRequired,
    problem: string.isRequired,
    reason: string.isRequired,
    value: string.isRequired
  })).isRequired,
  translate: func.isRequired
};

// Need to use 'connect' here because 'WithSurveyPartValidation' HOC depends on this prop.
const mapStateToProps = ({ surveySchemaPart }) => ({
  surveySchemaPart: surveySchemaPart.surveySchemaPartOptions
});

export default withLang(connect(mapStateToProps)(WithSurveyPartValidation(SurveyPartEditor)));
